import React from "react";
import {
  FaTruck,
  FaBoxOpen,
  FaUsers,
  FaExchangeAlt,
  FaShoppingCart,
  FaCashRegister,
  FaBook,
  FaMoneyBillWave,
  FaUniversity,
  FaClipboardCheck,
  FaFolderOpen,
  FaHandshake,
  FaChartLine,
  FaFileAlt,
  FaUserCog,
  FaUserTag,
  FaUserShield,
  FaCreditCard,
  FaSeedling,
  FaGlobeAsia,
  FaBullhorn,
  FaAddressBook,
  FaLandmark,
  FaFileUpload,
  FaReceipt,
  FaRupeeSign,
} from "react-icons/fa";

// Global-mode SideNav projection — SIDENAV_REDESIGN_PLAN.md Part 2.
//
// Each row is a pointer into routes.js's getModules() output: `path` is the exact relative
// module path routes.js uses (identical string in both the myFpo and mybusiness trees for every
// shared module — e.g. "accounts/purchase" — so it doubles as a stable, already-existing
// cross-service merge key; no separate id was needed). `query` appends a query string to the
// resolved link (used to split one module into two nav rows — Members/Share Transfer both point
// at accounts/share-capital's two tabs).
//
// No entityKey/roles/permission logic lives here anymore. hooks/useGlobalSideNav.js resolves each
// row by looking the real module up (by path) in that service's own getModules() output and
// reusing its already-computed `locked` state — routes.js stays the single source of truth for
// what a module requires, this file only decides which module to project into which Global
// section. (Previously this file hand-duplicated entityKey/roles per item, which is how
// mybusiness's AGM & Board roles list drifted from the real route's allowedRoles array; deriving
// from getModules() instead makes that class of drift impossible.)

export const GLOBAL_MY_ACCOUNT_ITEMS = [
  { path: "accounts/vendor", label: "Vendor Management", icon: <FaTruck /> },
  { path: "accounts/products", label: "Product Management", icon: <FaBoxOpen /> },
  { path: "accounts/share-capital", label: "Members", query: "?tab=tab1", icon: <FaUsers /> },
  { path: "accounts/share-capital", label: "Share Transfer", query: "?tab=tab2", icon: <FaExchangeAlt /> },
  { path: "accounts/purchase", label: "Purchase", icon: <FaShoppingCart /> },
  { path: "accounts/sales", label: "Sales", icon: <FaCashRegister /> },
  { path: "accounts/ledger", label: "Ledger", icon: <FaBook /> },
  { path: "accounts/cashbook", label: "Cash Book", icon: <FaMoneyBillWave /> },
  { path: "accounts/bank", label: "Bank", icon: <FaUniversity /> },
];

// Shared by External and Internal — same rows for both. Icons match the same concepts' icons in
// routes.js's Service-mode getModules() (Compliances/AGM & Board), so Global and Service mode
// read as one consistent visual language rather than two different sidebars.
export const GLOBAL_GOVERNANCE_ITEMS = [
  { path: "compliances", label: "Compliances", icon: <FaClipboardCheck /> },
  { path: "agm-board", label: "AGM & Board", icon: <FaHandshake /> },
];

export const GLOBAL_GROWTH_ITEMS = [
  { path: "business-plan", label: "Business Plan", icon: <FaChartLine /> },
  { path: "capacity-building", label: "Capacity Building", icon: <FaUsers /> },
  // Deliberately the My Account > Reports page (accounts/reports), not the separate top-level
  // "reports" module (routes.js's ReportsPage) — routes.js happens to define both under the same
  // "reports" permission entity but they are different pages; this preserves which one Global
  // mode has always linked to.
  { path: "accounts/reports", label: "Reports", icon: <FaFileAlt /> },
];

// Internal-only, platform-level. Not derived from getModules() — Users/User Types/Roles/
// Subscriptions aren't scoped to myFpo/mybusiness, they have no per-service structure to project
// from, so this stays its own small standalone list.
export const GLOBAL_ADMINISTRATION_ITEMS = [
  { label: "Users", path: "/users", entityKey: "users", icon: <FaUserCog /> },
  { label: "User Types", path: "/admin/user-types", entityKey: "roles", icon: <FaUserTag /> },
  { label: "Roles", path: "/admin/roles", entityKey: "roles", icon: <FaUserShield /> },
  { label: "Subscriptions", path: "/admin/subscription-plans", entityKey: "roles", icon: <FaCreditCard /> },
  // "Join Yuva Krushi" registrations from the FEED WORLD website (the fpo API allows System Administrators only)
  { label: "Yuva Krushi", path: "/admin/yuva-krushi", entityKey: "roles", icon: <FaSeedling /> },
  // "Join S.A.F.E. Mission" registrations from the FEED website (the fpo API allows System Administrators only)
  { label: "Safe Mission", path: "/admin/safe-mission", entityKey: "roles", icon: <FaGlobeAsia /> },
  // Export Readiness Score attempts from the FEED WORLD website (the fpo API allows System Administrators only)
  { label: "ERS Assessments", path: "/admin/ers-assessments", entityKey: "roles", icon: <FaClipboardCheck /> },
  // Every organisation's AGM & Board documents - delete one for good (the fpo API allows System Administrators only)
  { label: "AGM Documents", path: "/admin/agm-documents", entityKey: "roles", icon: <FaFolderOpen /> },
  // General notifications (dashboards + website home page) and customized FPO reminders (the fpo API allows System Administrators only)
  { label: "News & Alerts", path: "/admin/news-alerts", entityKey: "roles", icon: <FaBullhorn /> },
  // "Post Your Suggestion" from the website's My Directory (the fpo API allows System Administrators only)
  { label: "Directory Suggestions", path: "/admin/directory-suggestions", entityKey: "roles", icon: <FaAddressBook /> },
  // Government users' office area: opens and limits the Progress Report (the fpo API allows System Administrators only)
  { label: "Government Officers", path: "/admin/government-officers", entityKey: "roles", icon: <FaLandmark /> },
  // The Progress Report's monthly figures from Excel (the fpo API allows System Administrators only)
  { label: "Progress Report Data", path: "/admin/progress-report-data", entityKey: "roles", icon: <FaFileUpload /> },
  // Razorpay payments (membership fees, subscriptions) and their settings (the fpo API allows System Administrators only)
  { label: "Payments", path: "/admin/payments", entityKey: "roles", icon: <FaReceipt /> },
  { label: "Membership & Payment Settings", path: "/admin/payment-settings", entityKey: "roles", icon: <FaRupeeSign /> },
];
